import { z } from 'zod';
/**
 * The desktop-installer distribution contract.
 *
 * Installers are served by our own API rather than by GitHub Releases: the repository is
 * private, so a release asset URL is only fetchable with a token, and a candidate who has
 * never heard of GitHub must be able to click one link. The endpoints are public — the
 * desktop app is inert without an interview invitation, so an installer leaks nothing.
 */
/** The three OS families the release workflow packages for. */
export const downloadPlatformSchema = z.enum(['mac', 'win', 'linux']);
/**
 * `universal` is not a build target here; it is what a file with no architecture in its
 * name resolves to — an Intel dmg is published as `Proctoring-0.1.0.dmg`, and the
 * electron-updater feeds apply to every architecture of their platform.
 */
export const downloadArchSchema = z.enum(['arm64', 'x64', 'universal']);
/**
 * `feed` covers the `latest*.yml` electron-updater metadata. It is carried in the manifest
 * (rather than filtered out) so auto-update can be pointed at this API later by setting
 * `PROCTORING_UPDATE_FEED_URL` — no second endpoint, no second upload path.
 *
 * `checksums` is `SHA256SUMS.txt`, which a candidate is told to verify against.
 */
export const downloadFormatSchema = z.enum([
    'dmg',
    'zip',
    'exe',
    'deb',
    'rpm',
    'appimage',
    'feed',
    'checksums',
]);
export const downloadAssetSchema = z.object({
    platform: downloadPlatformSchema,
    arch: downloadArchSchema,
    format: downloadFormatSchema,
    filename: z.string().min(1),
    sizeBytes: z.number().int().nonnegative(),
    /**
     * Lowercase hex, or null when `SHA256SUMS.txt` did not cover the file. Null rather than
     * an empty string so the UI can tell "not published" from "published as empty" and omit
     * the verify hint instead of showing a hash nobody can check.
     */
    sha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/)
        .nullable(),
    /** Path relative to the API origin, e.g. `/downloads/0.1.0/Proctoring-0.1.0-arm64.dmg`. */
    url: z.string().min(1),
});
export const downloadVersionSchema = z.object({
    /** Bare semver, no `v` prefix — the directory name under the artifacts root. */
    version: z.string().min(1),
    /** Directory mtime: when the release landed on this server, not when it was tagged. */
    releasedAt: z.iso.datetime(),
    assets: z.array(downloadAssetSchema),
});
/**
 * Newest first. An empty `versions` array is a normal, expected state — a fresh
 * environment has published nothing yet — and every consumer must render it rather than
 * treat it as an error.
 */
export const downloadManifestSchema = z.object({
    versions: z.array(downloadVersionSchema),
});
//# sourceMappingURL=downloads.js.map